import {http} from '../http'
import * as facesApi from '../../coreApi/faces'
import {APIResponse} from '../types'
import {handleError} from '../handleError'
import {PersonTaggingCoverage, TaggingCoverage} from '../../../types'
import {RawPersonTaggingCoverage} from '../../coreApi/faces/types'

interface Params {
  clientId: number
  season: number
  personIds: number[]
}

export interface ServiceMethod {
  (params: Params): Promise<APIResponse<TaggingCoverage>>
}

const ERROR_PATH = 'api.faces.getTaggingCoverage()'
const GENERIC_ERROR =
  'The report could not be produced — coverage facts could not be retrieved. Please try again.'

// A 403 here means the signed-in user does not hold the staff role the coverage
// endpoint requires. That is the authoritative gate (the employee check in the
// UI is only a visibility affordance), and it needs its own message: "try again"
// is wrong advice for someone who will never be allowed. Exported so the view
// renders it verbatim without re-deriving the condition.
export const FORBIDDEN_ERROR = 'You are not authorized to view this report.'

// Rows requested per page. The whole filtered set is fetched before printing
// (D-F), so this bounds one response, not the report.
const COVERAGE_PAGE_SIZE = 200

// Bounded rather than while(true): the loop condition is otherwise entirely the
// server's to decide, and a totalCount that never settles would spin the tab.
// 200 pages at 200 rows is far past any camp's roster; reaching it means
// something upstream is wrong, and a truncated report is worse than an error.
const MAX_PAGES = 200

/* eslint-disable @typescript-eslint/no-explicit-any */
const isForbidden = (e: any): boolean => e?.response?.status === 403
/* eslint-enable @typescript-eslint/no-explicit-any */

// Normalize at the decode boundary so a field the server omitted can't reach the
// UI as `undefined` — the null-vs-value branches in the days-since-last-tag
// column (AC-10) distinguish "no recorded tag time" from "never tagged", and
// `undefined` belongs to neither.
const toCoverage = (raw: RawPersonTaggingCoverage): PersonTaggingCoverage => ({
  personId: Number(raw.personId),
  trained: raw.trained === true,
  trainedUTC: raw.trainedUTC ?? null,
  taggedPhotoCount: Number(raw.taggedPhotoCount ?? 0),
  lastTaggedUTC: raw.lastTaggedUTC ?? null,
  // D-R — a faces build predating PR 10738 omits this key. Normalizing it here
  // means "faces has not deployed yet" looks exactly like "this camper has no
  // qualifying photo" to every view, so no view code branches on deployment
  // state and AC-28 holds either way.
  lastTaggedPhotoCreatedUTC: raw.lastTaggedPhotoCreatedUTC ?? null,
})

/**
 * Coverage facts — training photo, season tag count, last tag time — for a list
 * of campers at one camp in one season.
 *
 * Pages internally to completion: the printed artifact must describe the whole
 * filtered set, so handing the caller page one would silently truncate the
 * report (D-F).
 */
export const getTaggingCoverage: ServiceMethod = async ({
  clientId,
  season,
  personIds,
}) => {
  try {
    if (personIds.length === 0) {
      return {result: 'success', data: {season, results: []}}
    }

    const results: PersonTaggingCoverage[] = []
    let resolvedSeason = season
    let page = 1

    while (page <= MAX_PAGES) {
      // eslint-disable-next-line no-await-in-loop
      const response = await facesApi.getTaggingCoverage(http, {
        ClientID: clientId,
        Season: season,
        PersonIDs: personIds,
        Page: page,
        PageSize: COVERAGE_PAGE_SIZE,
      })

      if (!response || !response.data) {
        throw new Error('Unsuccessful Get Tagging Coverage')
      }

      const body = response.data
      resolvedSeason = body.season ?? season
      const pageResults = body.results ?? []
      pageResults.forEach((raw) => results.push(toCoverage(raw)))

      // Stop on an empty page as well as on the count: a server that keeps
      // answering with no rows would otherwise never satisfy totalCount.
      if (pageResults.length === 0 || results.length >= Number(body.totalCount))
        break

      page += 1
    }

    return {result: 'success', data: {season: resolvedSeason, results}}
  } catch (e) {
    const failure = handleError(ERROR_PATH, GENERIC_ERROR, e)
    return isForbidden(e)
      ? {...failure, error: {...failure.error, message: FORBIDDEN_ERROR}}
      : failure
  }
}
